import type { ColumnsType } from "@similarweb/pro-logic-components";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { formatNumberWithCommas } from "app/pages/sales-intelligence/utils/numbers";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { CrmEnrichmentJobRunHistoryItem } from "../../types/types";
import { formatRunDate } from "../../utils/formatRunDate";

type CellValue = CrmEnrichmentJobRunHistoryItem[keyof CrmEnrichmentJobRunHistoryItem];

const formatNumber = (value: CellValue): string => {
    if (value === null || !is.number(value)) {
        return NO_DATA_HYPHEN;
    }

    return formatNumberWithCommas(value);
};

const PERMANENT_COLUMNS: ColumnsType<CrmEnrichmentJobRunHistoryItem> = [
    {
        dataIndex: "name",
        get title() {
            return translate("si.crm-enrichment.logs.column.rule");
        },
        width: 160,
    },
    {
        dataIndex: "runDate",
        get title() {
            return translate("si.crm-enrichment.logs.column.snapshot");
        },
        width: 160,
        render: (value: CellValue) => (value && is.string(value) ? formatRunDate(value) : NO_DATA_HYPHEN),
    },
    {
        dataIndex: "totalRecords",
        get title() {
            return translate("si.crm-enrichment.logs.column.total-records");
        },
        align: "right",
        render: (value: CellValue) => formatNumber(value),
    },
    {
        dataIndex: "succeeded",
        get title() {
            return translate("si.crm-enrichment.logs.column.succeeded");
        },
        align: "right",
        render: (value: CellValue) => formatNumber(value),
    },
    {
        dataIndex: "failed",
        get title() {
            return translate("si.crm-enrichment.logs.column.failed");
        },
        align: "right",
        render: (value: CellValue) => formatNumber(value),
    },
    {
        dataIndex: "credits",
        get title() {
            return translate("si.crm-enrichment.logs.column.credits");
        },
        align: "right",
        render: (value: CellValue) => formatNumber(value),
    },
];

const createScheduledJobColumns = (integrationName: Integration): ColumnsType<CrmEnrichmentJobRunHistoryItem> => [
    {
        dataIndex: "externalId",
        get title() {
            return translate(`si.crm-enrichment.logs.column.external-id.${integrationName}`);
        },
        render: (value: CellValue) => value ?? NO_DATA_HYPHEN,
    },
];

export const getEnrichmentLogsColumns = (
    onDownloadCsv: (runId: string) => void,
    integrationName: Integration,
    isShowExternalId: boolean,
): ColumnsType<CrmEnrichmentJobRunHistoryItem> => {
    const actionColumns: ColumnsType<CrmEnrichmentJobRunHistoryItem> = [
        {
            dataIndex: "runId",
            title: "",
            width: 120,
            align: "center",
            render: (_, row: CrmEnrichmentJobRunHistoryItem) => (
                <Tooltip
                    enabled={!row.hasLogsFile}
                    content={translate("si.crm-enrichment.logs.column.runId.disabled-tooltip")}
                >
                    <div>
                        <IconButton
                            type="flat"
                            iconName="download"
                            isDisabled={!row.hasLogsFile}
                            onClick={() => onDownloadCsv(row.runId)}
                            dataAutomation="enrichment-logs-download-csv"
                        >
                            {translate("si.crm-enrichment.logs.csv")}
                        </IconButton>
                    </div>
                </Tooltip>
            ),
        },
    ];

    if (isShowExternalId) {
        return [...PERMANENT_COLUMNS, ...createScheduledJobColumns(integrationName), ...actionColumns];
    }

    return [...PERMANENT_COLUMNS, ...actionColumns];
};
